import React, { useState, useEffect, useRef } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Copy, ExternalLink, Globe, Code } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface WidgetToken {
  id: string;
  token: string;
  domain_whitelist: string[] | null;
  is_active: boolean;
  created_at: string;
}

interface WidgetLoaderProps {
  orgId: string;
  token: string;
}

const WidgetLoader: React.FC<WidgetLoaderProps> = ({ orgId, token }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug component lifecycle
  useEffect(() => {
    console.log('🔧 WidgetLoader mounted with props:', { orgId, token });
    return () => {
      console.log('🔧 WidgetLoader unmounting');
    };
  }, []);

  useEffect(() => {
    console.log('🔧 WidgetLoader effect running:', { orgId, token, containerRef: !!containerRef.current });
    
    if (!orgId || !token) {
      console.log('❌ Missing orgId or token, setting error');
      setError('Missing organization ID or token');
      return;
    }

    const insertWidget = () => {
      if (!containerRef.current) {
        console.log('⏳ Container ref not ready, will retry...');
        return false;
      }

      console.log('✅ Container ref ready, inserting widget script');

      try {
        // Remove any existing widget scripts and clear containers
        const existingScripts = document.querySelectorAll('script[src*="hive360-widget.js"]');
        existingScripts.forEach(script => {
          console.log('🧹 Removing existing widget script');
          script.remove();
        });
        
        const existingWidgets = document.querySelectorAll('[id^="hive360-widget-"]:not(#hive360-widget-container)');
        existingWidgets.forEach(widget => {
          console.log('🧹 Removing existing widget:', widget.id);
          widget.remove();
        });

        // Create and configure the script element
        console.log('🔧 Creating script element...');
        const script = document.createElement('script');
        const scriptUrl = `${window.location.origin}/widget/hive360-widget.js`;
        console.log('🔧 Script URL:', scriptUrl);
        script.src = scriptUrl;
        script.setAttribute('data-org', orgId);
        script.setAttribute('data-token', token);
        script.async = true;
        console.log('🔧 Script configured with org:', orgId, 'token:', token);
        
        script.onload = () => {
          console.log('🎉 Widget script loaded successfully');
          setIsLoading(false);
        };
        script.onerror = (e) => {
          console.error('💥 Widget script failed to load:', e);
          setError('Failed to load widget script');
          setIsLoading(false);
        };
        
        // Insert script right before our container
        console.log('🔧 Inserting script into DOM...');
        console.log('🔧 Container parent exists:', !!containerRef.current.parentNode);
        containerRef.current.parentNode?.insertBefore(script, containerRef.current);
        console.log('📜 Widget script inserted into DOM');
        
        return true;
      } catch (err) {
        console.error('💥 Error inserting widget:', err);
        setError('Error inserting widget');
        return false;
      }
    };

    // Try to insert immediately, then retry if needed
    let retryCount = 0;
    const maxRetries = 10;
    
    const tryInsert = () => {
      if (insertWidget()) {
        return; // Success
      }
      
      retryCount++;
      if (retryCount < maxRetries) {
        console.log(`🔄 Retry ${retryCount}/${maxRetries}`);
        setTimeout(tryInsert, 100);
      } else {
        console.error('🚫 Max retries reached, giving up');
        setError('Failed to initialize widget after multiple attempts');
        setIsLoading(false);
      }
    };

    tryInsert();

    // Cleanup function
    return () => {
      console.log('🧹 WidgetLoader cleanup running');
      const scriptToRemove = document.querySelector('script[src*="hive360-widget.js"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
      const widgetToRemove = document.querySelector('[id^="hive360-widget-"]:not(#hive360-widget-container)');
      if (widgetToRemove) {
        widgetToRemove.remove();
      }
    };
  }, [orgId, token]);

  if (error) {
    return (
      <div style={{ minHeight: '200px', padding: '20px', textAlign: 'center' }}>
        <div style={{ color: 'red' }}>Error: {error}</div>
        <button 
          onClick={() => {
            setError(null);
            setIsLoading(true);
            // Force re-run of effect by changing a dependency
            window.location.reload();
          }}
          style={{
            marginTop: '10px',
            padding: '8px 16px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ minHeight: '200px', padding: '20px', textAlign: 'center' }}>
      {isLoading && <div>Loading widget...</div>}
    </div>
  );
};

export default function WidgetTestPage() {
  const { organization, loading } = useOrganization();
  const [tokens, setTokens] = useState<WidgetToken[]>([]);
  const [selectedToken, setSelectedToken] = useState<string>('');
  const [loadingTokens, setLoadingTokens] = useState(true);

  // Load widget tokens
  useEffect(() => {
    if (!loading && organization) {
      const loadTokens = async () => {
        if (!organization) return;
        
        try {
          const { data, error }: { data: WidgetToken[] | null; error: any } = await supabase
            .from('hive360_tokens')
            .select('*')
            .eq('organization_id', organization.id)
            .eq('is_active', true)
            .order('created_at', { ascending: false });

          if (error) throw error;
          
          const tokenList = data || [];
          setTokens(tokenList);
          
          // Auto-select the first token
          if (tokenList.length > 0) {
            setSelectedToken(tokenList[0].token);
          }
        } catch (error) {
          console.error('Error loading tokens:', error);
          toast.error('Failed to load widget tokens');
        } finally {
          setLoadingTokens(false);
        }
      };

      loadTokens();
    }
  }, [organization, loading]);

  const copyEmbedCode = (token: string) => {
    const embedCode = `<script 
  src="${window.location.origin}/widget/hive360-widget.js" 
  data-org="${organization?.id}" 
  data-token="${token}"
  async>
</script>`;
    
    navigator.clipboard.writeText(embedCode);
    toast.success('Embed code copied to clipboard');
  };

  const getEmbedCode = (token: string) => {
    return `<script 
  src="${window.location.origin}/widget/hive360-widget.js" 
  data-org="${organization?.id}" 
  data-token="${token}"
  async>
</script>`;
  };

  if (loading || loadingTokens) {
    return <div>Loading test page...</div>;
  }

  if (tokens.length === 0) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-start gap-4 flex-1">
          <div className="p-4 rounded-full flex items-center justify-center bg-orange-100">
            <Globe className="h-6 w-6 text-orange-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Widget Test Page
            </h1>
            <p className="text-muted-foreground">
              Test your Hive360 widget in a simulated environment
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>No Widget Tokens Found</CardTitle>
            <CardDescription>
              You need to generate a widget token before you can test the widget.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href="/admin/settings" className="gap-2">
                <Code className="h-4 w-4" />
                Go to Settings → Hive360
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start gap-4 flex-1">
        <div className="p-4 rounded-full flex items-center justify-center bg-orange-100">
          <Globe className="h-6 w-6 text-orange-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Widget Test Page
          </h1>
          <p className="text-muted-foreground">
            Test your Hive360 widget in a simulated website environment
          </p>
        </div>
      </div>

      {/* Token Selection */}
      {tokens.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Select Widget Token</CardTitle>
            <CardDescription>
              Choose which token to test with
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedToken} onValueChange={setSelectedToken}>
              <SelectTrigger>
                <SelectValue placeholder="Select a token" />
              </SelectTrigger>
              <SelectContent>
                {tokens.map((token) => (
                  <SelectItem key={token.id} value={token.token}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{token.token}</span>
                      <Badge variant="secondary">
                        {token.domain_whitelist?.length 
                          ? `${token.domain_whitelist.join(', ')}`
                          : 'Any domain'
                        }
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Embed Code */}
      <Card>
        <CardHeader>
          <CardTitle>Widget Embed Code</CardTitle>
          <CardDescription>
            This is the code that would be embedded on your website
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gray-50 border rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre>{getEmbedCode(selectedToken)}</pre>
          </div>
          <Button 
            onClick={() => copyEmbedCode(selectedToken)}
            variant="outline"
            className="w-full"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Embed Code
          </Button>
        </CardContent>
      </Card>

      {/* Live Widget Test */}
      <Card>
        <CardHeader>
          <CardTitle>Live Widget Preview</CardTitle>
          <CardDescription>
            This shows how the widget will appear on your website
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-6 bg-white min-h-[400px]">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              {organization?.name} - Program Enrollment
            </h3>
            <p className="text-gray-600 mb-6">
              Enroll in our programs below:
            </p>
            
            {/* Widget Container */}
            <div id="hive360-widget-container">
              {selectedToken && organization?.id ? (
                <WidgetLoader orgId={organization.id} token={selectedToken} />
              ) : (
                <div style={{ minHeight: '200px', padding: '20px', textAlign: 'center' }}>
                  <div>
                    {!selectedToken ? 'No token selected' : !organization?.id ? 'No organization ID' : 'Loading...'}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> The widget above shows how it will appear on your website. 
              Test the enrollment flow to ensure everything works correctly.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* External Test */}
      <Card>
        <CardHeader>
          <CardTitle>External Website Test</CardTitle>
          <CardDescription>
            For more realistic testing, try the widget on an external site
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              To test on your actual website:
            </p>
            <ol className="text-sm space-y-2 list-decimal list-inside text-muted-foreground">
              <li>Copy the embed code above</li>
              <li>Paste it into your website's HTML</li>
              <li>Make sure your domain is in the whitelist (or leave whitelist empty for testing)</li>
              <li>Test program enrollment and verify analytics</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}